import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Shield,
  FileText,
  Cookie,
  Eye,
  Lock,
  Globe,
  RefreshCcw,
  UserCheck,
  Database,
  Users,
  FolderLock,
  Mail,
  AlertTriangle,
  Clock
} from 'lucide-react';

const sections = [
  { id: 'introduction', title: 'Introduction', icon: FileText },
  { id: 'data-we-collect', title: 'Data We Collect', icon: Database },
  { id: 'how-we-use', title: 'How We Use Your Data', icon: Users },
  { id: 'cookies', title: 'Cookies & Tracking', icon: Cookie },
  { id: 'security', title: 'Data Security', icon: Lock },
  { id: 'sharing', title: 'Data Sharing & Disclosure', icon: Shield },
  { id: 'international', title: 'International Transfers', icon: Globe },
  { id: 'rights', title: 'Your Rights', icon: UserCheck },
  { id: 'retention', title: 'Data Retention', icon: Clock },
  { id: 'children', title: 'Children\'s Privacy', icon: AlertTriangle },
  { id: 'changes', title: 'Changes to This Policy', icon: RefreshCcw },
  { id: 'contact', title: 'Contact Us', icon: Mail }
];

const lastUpdated = new Date().toLocaleDateString(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
});

const Privacy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Tasks IT Services</title>
        <meta
          name="description"
          content="Learn how Tasks IT Services collects, uses, secures, and protects your personal information."
        />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900" />
        <div className="pointer-events-none absolute -top-40 -left-40 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="pointer-events-none absolute -bottom-40 -right-40 w-[30rem] h-[30rem] bg-blue-600/20 rounded-full blur-3xl" />

        {/* Hero */}
        <section className="relative pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl shadow-xl mb-6">
                <Shield className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-5xl font-bold mb-3">Privacy Policy</h1>
              <p className="text-blue-100 max-w-2xl mx-auto">
                Your privacy matters. This policy explains what we collect, why we collect it, and how we safeguard your data.
              </p>
              <div className="mt-4 text-sm text-blue-200">
                Last updated: {lastUpdated}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="relative pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
            {/* TOC */}
            <aside className="lg:col-span-4 xl:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 }}
                className="lg:sticky lg:top-24"
              >
                <div className="backdrop-blur-xl bg-white/10 border border-white/15 rounded-2xl p-6 text-white shadow-2xl">
                  <h3 className="text-lg font-semibold mb-4">On this page</h3>
                  <nav className="space-y-2">
                    {sections.map(({ id, title }) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="block px-3 py-2 rounded-lg text-sm text-blue-100 hover:text-white hover:bg-white/10 transition"
                      >
                        {title}
                      </a>
                    ))}
                  </nav>
                </div>
              </motion.div>
            </aside>

            {/* Main */}
            <div className="lg:col-span-8 xl:col-span-9 space-y-6">
              {sections.map(({ id, title, icon: Icon }, idx) => (
                <motion.section
                  key={id}
                  id={id}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.45, delay: 0.02 * idx }}
                  className="relative group"
                >
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/20 via-cyan-500/10 to-purple-500/20 blur-xl opacity-50 group-hover:opacity-80 transition" />
                  <div className="relative backdrop-blur-xl bg-white/10 border border-white/15 rounded-3xl p-6 md:p-8 text-white shadow-2xl">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <h2 className="text-2xl font-bold">{title}</h2>
                    </div>

                    {id === 'introduction' && (
                      <p className="text-blue-100 leading-relaxed">
                        This Privacy Policy explains how Tasks IT Services (“Company”, “we”, “us”) collects, uses, and protects
                        personal information obtained through our website and related services. Use of our services signifies agreement
                        with this Policy and our <Link to="/terms" className="text-cyan-300 underline hover:text-cyan-200">Terms of Service</Link>.
                      </p>
                    )}

                    {id === 'data-we-collect' && (
                      <ul className="list-disc pl-5 space-y-2 text-blue-100">
                        <li>Contact details (name, email, phone) you submit via forms.</li>
                        <li>Project scope details, technical requirements, and site information shared during an engagement.</li>
                        <li>Technical data (IP address, browser, device, timestamps).</li>
                        <li>Usage metrics (pages visited, interactions, referrer).</li>
                        <li>Optional newsletter subscription email.</li>
                      </ul>
                    )}

                    {id === 'how-we-use' && (
                      <ul className="list-disc pl-5 space-y-2 text-blue-100">
                        <li>Provide and improve services and platform reliability.</li>
                        <li>Respond to client service enquiries and manage project delivery.</li>
                        <li>Analyze aggregated usage to enhance user experience.</li>
                        <li>Send limited operational or security notices.</li>
                        <li>Comply with legal or regulatory obligations.</li>
                      </ul>
                    )}

                    {id === 'cookies' && (
                      <p className="text-blue-100 leading-relaxed">
                        We may use essential and performance cookies or similar technologies to maintain session quality and gather
                        anonymized analytics. You can adjust browser settings to refuse non-essential cookies. Blocking some cookies
                        may impact site functionality.
                      </p>
                    )}

                    {id === 'security' && (
                      <p className="text-blue-100 leading-relaxed">
                        We implement administrative, technical, and physical safeguards (access controls, encryption in transit, audit logging)
                        to protect data. No method is 100% secure; we encourage responsible disclosure of potential issues.
                      </p>
                    )}

                    {id === 'sharing' && (
                      <ul className="list-disc pl-5 space-y-2 text-blue-100">
                        <li>Service providers (infrastructure, analytics) under contractual confidentiality.</li>
                        <li>Technology vendors and sub-contractors engaged in delivering your project, under contractual NDA.</li>
                        <li>Legal authorities when required by law or to protect rights.</li>
                        <li>We do not sell personal data.</li>
                      </ul>
                    )}

                    {id === 'international' && (
                      <p className="text-blue-100 leading-relaxed">
                        Data may be processed in jurisdictions with different laws. We apply safeguards (standard contractual clauses or equivalent)
                        to maintain protection consistent with applicable regulations.
                      </p>
                    )}

                    {id === 'rights' && (
                      <ul className="list-disc pl-5 space-y-2 text-blue-100">
                        <li>Access: Request a copy of personal data.</li>
                        <li>Correction: Update inaccurate information.</li>
                        <li>Deletion: Request removal where legally permissible.</li>
                        <li>Restriction/Objection: Limit certain processing activities.</li>
                        <li>Withdrawal: Opt out of non-essential communications.</li>
                      </ul>
                    )}

                    {id === 'retention' && (
                      <p className="text-blue-100 leading-relaxed">
                        We retain data only as long as necessary for the purposes described or required by law. Client project records are retained for the duration of the engagement and up to 2 years after project completion. Aggregated or anonymized usage data may be retained for analytics.
                      </p>
                    )}

                    {id === 'children' && (
                      <p className="text-blue-100 leading-relaxed">
                        Our services are not directed to children under 16. If we learn we have collected data from a minor without verifiable
                        parental consent, we will take steps to delete it.
                      </p>
                    )}

                    {id === 'changes' && (
                      <p className="text-blue-100 leading-relaxed">
                        We may update this Policy. Material changes will be indicated by updating the “Last updated” date. Continued use after
                        changes constitutes acceptance.
                      </p>
                    )}

                    {id === 'contact' && (
                      <div className="text-blue-100 leading-relaxed space-y-2">
                        <p>For privacy inquiries or data rights requests, contact:</p>
                        <p>
                          Email: <a href="mailto:GlennRosas@hotmail.com" className="text-cyan-300 underline hover:text-cyan-200">
                            GlennRosas@hotmail.com
                          </a>
                        </p>
                        <p className="text-sm text-blue-200/80">We aim to respond within 2–3 business days.</p>
                      </div>
                    )}
                  </div>
                </motion.section>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center text-blue-200 text-sm mt-10"
              >
                © {new Date().getFullYear()} Tasks IT Services. All rights reserved. See also our{' '}
                <Link to="/terms" className="text-cyan-300 underline hover:text-cyan-200">Terms of Service</Link>.
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Privacy;